/**
 * Trunnion AI - Tour Context Provider
 * Manages the active state and step navigation for the interactive onboarding tour.
 * Wrap the application tree with this provider; it renders <OnboardingTour /> when
 * the tour is active so consuming components only need to call context helpers.
 */

import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { TOUR_STEPS, type TourStep } from '@/config/tourSteps';
import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingTour } from '@/components/onboarding/OnboardingTour';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface TourContextValue {
  /** Whether the tour is currently being displayed */
  isActive: boolean;
  /** Index of the step currently being shown */
  currentStepIndex: number;
  /** The full list of tour steps */
  steps: TourStep[];
  /** The step object for the current index (convenience) */
  currentStep: TourStep | null;
  /** Total number of steps */
  totalSteps: number;
  /** Advance to the next step (or complete if on the last step) */
  next: () => void;
  /** Go back to the previous step */
  prev: () => void;
  /** Skip the tour entirely */
  skip: () => void;
  /** Mark the tour as completed */
  complete: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const TourContext = createContext<TourContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const { shouldShowTour, skipTour, completeTour } = useOnboarding();

  const [isActive, setIsActive] = useState(true);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  // Derived
  const steps = TOUR_STEPS;
  const totalSteps = steps.length;
  const currentStep = isActive && currentStepIndex < totalSteps ? steps[currentStepIndex] : null;

  // --- Navigation helpers ---------------------------------------------------

  const next = useCallback(() => {
    setCurrentStepIndex((prev) => {
      if (prev >= totalSteps - 1) {
        // Last step -- trigger completion
        setIsActive(false);
        setShowCelebration(true);
        completeTour();
        return prev;
      }
      return prev + 1;
    });
  }, [totalSteps, completeTour]);

  const prev = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const skip = useCallback(() => {
    setIsActive(false);
    setShowCelebration(false);
    skipTour();
  }, [skipTour]);

  const complete = useCallback(() => {
    setIsActive(false);
    setShowCelebration(true);
    completeTour();
  }, [completeTour]);

  const dismissCelebration = useCallback(() => {
    setShowCelebration(false);
  }, []);

  // --- Context value --------------------------------------------------------

  const value = useMemo<TourContextValue>(
    () => ({
      isActive: shouldShowTour && isActive,
      currentStepIndex,
      steps,
      currentStep: shouldShowTour && isActive ? currentStep : null,
      totalSteps,
      next,
      prev,
      skip,
      complete,
    }),
    [shouldShowTour, isActive, currentStepIndex, steps, currentStep, totalSteps, next, prev, skip, complete],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <OnboardingTour showCelebration={showCelebration} onDismissCelebration={dismissCelebration} />
    </TourContext.Provider>
  );
}
